export type Product = {
  id: number
  display: string
  unit: string
  isInStorage: boolean | null
}

export type Ingredient = {
  id: number
  tenantId?: number
  quantity: number | null
  productId: number
  recipeId: number
  freezer: boolean | null
}

export type Recipe = {
  id: number
  tenantId: number
  /** Present on public cookbook payloads for "other household" labels. */
  tenantName?: string | null
  /** Avatar for the recipe's household; use `/api/tenants/:id/image`. */
  tenantHasImage?: boolean
  display: string | null
  url: string | null
  instructions: string | null
  season: string | null
  main: string | null
  course: string | null
  portionsEat: number | null
  portionsFreezer: number | null
  isToBuy: boolean | null
  isToCook: boolean | null
  isWishlist: boolean | null
  isArchived: boolean | null
  preparationMinutes: number | null
  /** Photo stored as bytea; use `/api/recipes/:id/image` when true. */
  hasImage?: boolean
  /** `themealdb` | `pexels` when bytes came from that source; null for uploads. */
  imageSource?: string | null
  /** Photographer name (Pexels attribution); null for other sources. */
  imagePhotographer?: string | null
  imagePhotographerUrl?: string | null
  /** Pexels photo page URL for attribution. */
  imagePageUrl?: string | null
}

/** Who can see a list in the public cookbook. */
export type ListAccessLevel = 'tenant' | 'follower' | 'everyone'

/** Synthetic cookbook section for recipes not linked to any list (own tenant only). */
export const ORPHAN_LIST_ID = -1
export const ORPHAN_LIST_DISPLAY = 'Zonder lijst'

export function isOrphanListId(id: number | null | undefined): boolean {
  return id === ORPHAN_LIST_ID
}

export type Plan = {
  id: number
  display: string
  multiplier: number | null
  plannedAt: string | null
  consumedAt: string | null
  order: number | null
  orderPosition: number | null
  orderId: number | null
  recipeId: number | null
  portionsEat: number | null
  portionsFreezer: number | null
  portionsConsumed: number | null
  multiplierFreezer: number | null
}

export type CartItemStatus = 'ok' | 'to_remove' | 'not_found'

export type CartItem = {
  productId: number
  quantity: number
  /** Agent notice for the shopper (e.g. "1 te veel"); empty when fine. */
  messageAgent?: string
  /** Sync outcome set by Collect&Go agent. */
  status?: CartItemStatus
  /** Optional note from the user (e.g. "koop een grote zak"). */
  messageHuman?: string
  product?: Product
}

export type Shop = 'manual' | 'cogo'

export type Order = {
  id: number
  slug: string | null
  display: string | null
  cartCustomized?: boolean
  /** null = inherit tenant defaultShop */
  shop?: Shop | null
  /** Manual sort among planning columns. */
  position?: number | null
  createdAt: string | null
  plannedAt: string | null
  orderedAt: string | null
  deliveredAt: string | null
  archivedAt: string | null
}

export type List = {
  id: number
  tenantId: number
  /** Present on public cookbook payloads. */
  tenantName?: string | null
  /** Avatar for the list's household; use `/api/tenants/:id/image`. */
  tenantHasImage?: boolean
  slug: string | null
  display: string
  accessLevel: ListAccessLevel
  /** Cover stored as bytea; use `/api/lists/:id/image` when true. */
  hasImage: boolean
  position: number | null
  createdAt: string | null
  archivedAt: string | null
}

export type FullIngredient = Ingredient & { product: Product }
export type FullRecipe = Recipe & { ingredients: FullIngredient[] }
export type FullPlan = Plan & { recipe: FullRecipe | null }
export type FullOrder = Order & { plans: FullPlan[] }
export type FullList = List & { recipes: FullRecipe[] }

/** @deprecated Use Order */
export type Collection = Order
/** @deprecated Use FullOrder */
export type FullCollection = FullOrder

export type Ctx = {
  today: Plan[]
  recipes: Recipe[]
  ingredients: Ingredient[]
  products: Product[]
}
